// File: frontend/src/components/PdfExportButton.tsx
// Button + progress dialog for exporting a report as PDF.
// PDF generation runs in a Web Worker — main thread stays responsive.

import React, { useState, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ScienceIcon from '@mui/icons-material/Science';
import { Report, HybridIOC_V2 } from '../types';
import { useApi } from '../hooks/useApi';
import { generateArticlePdf, PdfExportData, CVEForPdf } from '../utils/pdfExport';
import { cyber } from '../theme';

interface PdfExportButtonProps {
  report: Report;
}

const PdfExportButton: React.FC<PdfExportButtonProps> = ({ report }) => {
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stepLabel, setStepLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const { simpleTextFetcher, authenticatedFetcher } = useApi();

  const handleProgress = useCallback((step: string, pct: number) => {
    setStepLabel(step);
    setProgress(pct);
  }, []);

  const handleExport = useCallback(async () => {
    setIsGenerating(true);
    setError(null);
    setProgress(0);
    setStepLabel('Fetching data...');

    try {
      // ─── Fetch all text/JSON data in parallel ────────────────────────
      handleProgress('Fetching report data...', 5);

      const fetchText = async (url: string | null | undefined): Promise<string | null> => {
        if (!url) return null;
        try { return await simpleTextFetcher(url); } catch { return null; }
      };

      const [summaryMarkdown, ttpMarkdown, fiveWMarkdown, iocJsonStr, stixJsonStr, mitreLayerStr, mindmapCode] =
        await Promise.all([
          fetchText(report.blobPathSummary),
          fetchText(report.blobPathTTPsTable),
          fetchText(report.blobPathFiveWhats),
          fetchText(report.blobPathAdvancedIOCs),
          fetchText(report.blobPathStixBundle),
          fetchText(report.blobPathMitreLayer),
          fetchText(report.blobPathMindmapCode),
        ]);

      // Fetch CVEs via authenticated API
      let cves: CVEForPdf[] = [];
      try {
        const cveResponse = await authenticatedFetcher(`/api/cves/by-article/${report.id}`);
        cves = cveResponse?.cves || [];
      } catch { /* CVEs may not exist */ }

      // Parse IOCs
      let iocs: HybridIOC_V2[] = [];
      if (iocJsonStr) {
        try { iocs = JSON.parse(iocJsonStr); } catch { /* ignore */ }
      }

      // ─── Generate PDF (in Web Worker) ──────────────────────────────
      const pdfData: PdfExportData = {
        report,
        summaryMarkdown,
        ttpMarkdown,
        fiveWMarkdown,
        iocs,
        cves,
        stixJsonString: stixJsonStr,
        mitreLayerJsonString: mitreLayerStr,
        mindmapCode,
      };

      await generateArticlePdf(pdfData, handleProgress);

      handleProgress('Done!', 100);
      setTimeout(() => setOpen(false), 1000);
    } catch (err) {
      console.error('PDF export error:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred during PDF generation.');
    } finally {
      setIsGenerating(false);
    }
  }, [report, simpleTextFetcher, authenticatedFetcher, handleProgress]);

  const handleOpen = () => {
    setOpen(true);
    setProgress(0);
    setStepLabel('');
    setError(null);
    setTimeout(handleExport, 200);
  };

  const handleClose = () => {
    if (!isGenerating) {
      setOpen(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        startIcon={<PictureAsPdfIcon />}
        endIcon={<ScienceIcon sx={{ fontSize: '0.9rem !important' }} />}
        onClick={handleOpen}
        sx={{
          background: cyber.gradient,
          '&:hover': {
            background: cyber.gradientHover,
          },
          fontWeight: 600,
        }}
      >
        Export PDF (Beta)
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PictureAsPdfIcon color="primary" />
          <Typography variant="h6" component="span">
            Generating PDF Report
          </Typography>
          <Typography
            variant="caption"
            sx={{
              bgcolor: 'warning.main',
              color: 'warning.contrastText',
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontWeight: 700,
              fontSize: '0.65rem',
              ml: 0.5,
            }}
          >
            BETA
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, py: 0 }}>
            <Typography variant="caption">
              This is a beta feature. PDF layout and content may vary.
            </Typography>
          </Alert>
          <Box sx={{ mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 4,
                mb: 1.5,
                '& .MuiLinearProgress-bar': {
                  background: cyber.gradient,
                  borderRadius: 4,
                },
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {stepLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {Math.round(progress)}% complete
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {progress === 100 && !error && (
            <Alert severity="success" sx={{ mt: 2 }}>
              PDF generated successfully! Your download should start automatically.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isGenerating && progress < 100}>
            {progress === 100 ? 'Close' : 'Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PdfExportButton;
